import React, { useEffect, useState } from "react";
import { Menu, X, Moon, Sun } from "lucide-react";
import BrandLogo from "./BrandLogo";
import { NAV_LINKS } from "../mock/mock";
import { Button } from "./ui/button";
import { useTheme } from "../context/ThemeContext";
import { SOCIAL_LINKS } from "./SocialIcons";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { dark, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg shadow-[0_4px_30px_rgba(14,127,168,0.08)] py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-2.5 group"
          >
            <BrandLogo
              variant={!scrolled || dark ? "white" : "dark"}
              className="w-9 h-9 transition-all"
            />
            <span
              className={`font-display font-800 text-lg tracking-tight transition-colors ${
                scrolled ? "text-slate-900 dark:text-white" : "text-white"
              }`}
            >
              Sharkbowl Games
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => scrollTo(l.href)}
                className={`text-sm font-semibold relative transition-colors hover:text-cyan-500 ${
                  scrolled ? "text-slate-700 dark:text-white/80" : "text-white/90"
                }`}
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105 ${
                scrolled
                  ? "bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-cyan-300"
                  : "bg-white/20 text-white"
              }`}
            >
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Button
              onClick={() => scrollTo("#contact")}
              className="rounded-full bg-teal-600 hover:bg-teal-700 text-white px-6 shadow-lg shadow-teal-600/20 transition-transform hover:-translate-y-0.5"
            >
              Let's talk
            </Button>
          </nav>

          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              className={`w-9 h-9 rounded-full flex items-center justify-center ${
                scrolled ? "text-slate-900 dark:text-white" : "text-white"
              }`}
            >
              {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              className={`p-2 rounded-lg ${scrolled ? "text-slate-900 dark:text-white" : "text-white"}`}
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-7 h-7" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay menu */}
      <div
        className={`fixed inset-0 z-[60] ocean-gradient transition-all duration-500 md:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="flex justify-end p-6">
          <button onClick={() => setOpen(false)} className="text-white p-2" aria-label="Close menu">
            <X className="w-8 h-8" />
          </button>
        </div>
        <div className="flex flex-col items-center justify-center gap-8 h-[70%]">
          {NAV_LINKS.map((l, i) => (
            <button
              key={l.href}
              onClick={() => scrollTo(l.href)}
              className="font-display text-3xl font-bold text-white hover:scale-110 transition-transform"
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              {l.label}
            </button>
          ))}
          <div className="flex gap-6 mt-6">
            {SOCIAL_LINKS.map(({ name, Icon, href }) => (
              <a
                key={name}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={name}
                onClick={() => setOpen(false)}
                className="text-white/90 hover:text-white hover:scale-110 transition-transform"
              >
                <Icon className="w-6 h-6" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}